<template>
  <v-data-table
    id="users-table-list"
    :loading="loading || searching"
    :loading-text="$t('honda.users.list.loading')"
    :headers="headers"
    :items="filteredUsers"
    :item-class="getRowClass"
    :items-per-page="10"
    :sort-by="['created']"
    :sort-desc="[true]"
    class="elevation-0"
    :footer-props="{
      showFirstLastPage: true,
      firstIcon: 'fas fa-angle-double-left',
      lastIcon: 'fas fa-angle-double-right',
      prevIcon: 'fas fa-angle-left',
      nextIcon: 'fas fa-angle-right',
      'items-per-page-text': $t('honda.registration.list.footer_num_x_page_client'),
      'items-per-page-options': [10, 25, 50, -1],
    }"
    :no-data-text="
      $t(
        search || (usersList && usersList.length > 0) ? 'honda.users.list.no_data_search' : 'honda.users.list.no_data',
        {
          search,
        },
      )
    "
    :no-results-text="$t('honda.users.list.no_data_search')"
    data-cy="users-table-list"
    @click:row="selectUser">
    <template #footer.page-text="props">
      {{ props.pageStart }}-{{ props.pageStop }} {{ $t('honda.registration.list.of') }}
      {{ props.itemsLength }}
    </template>

    <template v-slot:item.created="{item}">
      <span class="text-item">{{ item.created | formatDate }}</span>
    </template>

    <template v-slot:item.ownerLogin="{item}">
      <div class="customer py-3 lbl-registration-bold" data-cy="customer-name-email">
        {{ item.fullName }}<br />
        <span class="customer-email">{{ item.ownerLogin }}</span>
      </div>
    </template>

    <template v-slot:item.phone="{item}">
      <span class="text-item" data-cy="customer-phone">{{ item.phone | formatPhone }}</span>
    </template>

    <template v-slot:item.deviceCount="{item}">
      <span class="text-item">{{ item.deviceCount }}</span>
    </template>

    <template v-slot:item.containerCount="{item}">
      <span class="text-item">{{ item.containerCount }}</span>
    </template>
  </v-data-table>
</template>

<script>
import * as Sentry from '@sentry/vue'
import {debounce} from 'lodash'

import api from '@/api'
import {
  buildSupervisorSearchQueries,
  createSupervisorMatcher,
  isSupervisorSearchWidened,
  mergeSupervisors,
} from '@/utils/supervisor-search'

const SEARCH_DEBOUNCE_MS = 400
const DEFAULT_LIST_MAX = 5000
const SEARCH_QUERY_MAX = 200

function fetchSupervisors(filters, maxPage) {
  return new Promise((resolve, reject) => {
    api.getSupervisors(filters, (err, resp) => (err ? reject(err) : resolve(resp || [])), 1, maxPage)
  })
}

export default {
  name: 'UsersTable',
  components: {},
  props: ['search', 'forcedGroup'],
  data() {
    return {
      loading: false,
      headers: [
        {
          text: this.$t('honda.users.list.lbl-client'),
          align: 'start',
          value: 'ownerLogin',
          class: 'text-header',
          width: '35%',
          sortable: false,
        },
        {
          text: this.$t('honda.users.list.lbl-phone'),
          align: 'left',
          value: 'phone',
          class: 'text-header',
          width: '20%',
          sortable: false,
        },
        {
          text: this.$t('honda.users.list.lbl-vehicles'),
          align: 'center',
          value: 'containerCount',
          class: 'text-header',
          width: '15%',
          sortable: false,
        },
        {
          text: this.$t('honda.users.list.lbl-mapit'),
          align: 'center',
          value: 'deviceCount',
          class: 'text-header',
          width: '15%',
          sortable: false,
        },
        {
          text: this.$t('honda.users.list.lbl-creation'),
          align: 'left',
          value: 'created',
          class: 'text-header',
          width: '15%',
          sortable: true,
        },
      ],
      usersList: [],
      listRequestId: 0,
      // Results of the server side search (the default list only contains the latest DEFAULT_LIST_MAX supervisors)
      searchResults: [],
      // Term the searchResults belong to: while a new term is pending they are narrowed locally instead of cleared
      searchResultsTerm: '',
      searching: false,
      searchRequestId: 0,
    }
  },
  watch: {
    forcedGroup: function () {
      // Nothing of the previous group may stay on screen
      this.usersList = []
      this.cancelServerSearch()
      this.getUsersList()
      this.searchOnServer()
    },
    search: function () {
      // Late responses of the previous term are ignored, but its results stay (narrowed) until the new ones arrive
      this.searchRequestId++
      this.debouncedSearchOnServer()
    },
  },
  computed: {
    localMatches() {
      return this.search ? this.usersList.filter(createSupervisorMatcher(this.search)) : this.usersList
    },
    serverMatches() {
      if (this.searchResultsTerm === this.search) return this.searchResults
      return this.searchResults.filter(createSupervisorMatcher(this.search))
    },
    filteredUsers() {
      return this.search ? mergeSupervisors(this.serverMatches, this.localMatches) : this.usersList
    },
    // Mapit2 caps the default list at DEFAULT_LIST_MAX rows, so a shorter list is the whole group and the local
    // filter already covers every supervisor; a full one may have left older supervisors out
    isDefaultListTruncated() {
      return this.loading || this.usersList.length >= DEFAULT_LIST_MAX
    },
    group() {
      return this.$store.getters.getGroup
    },
    groupId() {
      return `${this.forcedGroup || this.group.legacy.id}`
    },
  },
  created() {
    this.debouncedSearchOnServer = debounce(this.searchOnServer, SEARCH_DEBOUNCE_MS)
    this.getUsersList()
  },
  beforeDestroy() {
    this.debouncedSearchOnServer.cancel()
    this.cancelServerSearch()
  },
  methods: {
    async getUsersList() {
      const requestId = ++this.listRequestId
      this.loading = true
      try {
        const users = await fetchSupervisors({groupId: this.groupId}, DEFAULT_LIST_MAX)
        // A slow response of a previous group must not replace the current one
        if (requestId === this.listRequestId) this.usersList = users
      } catch (error) {
        Sentry.captureException(error instanceof Error ? error : new Error('Supervisors list failed'), {
          extra: {groupId: this.groupId, error},
        })
      } finally {
        if (requestId === this.listRequestId) this.loading = false
      }
    },
    cancelServerSearch() {
      // Any response of a previous search is ignored from now on
      this.searchRequestId++
      this.searchResults = []
      this.searchResultsTerm = ''
      this.searching = false
    },
    async searchOnServer() {
      const term = this.search
      const queries = buildSupervisorSearchQueries(term)
      if (!queries.length || !this.isDefaultListTruncated) {
        this.cancelServerSearch()
        return
      }

      const requestId = ++this.searchRequestId
      const groupId = this.groupId
      this.searching = true
      const responses = await Promise.allSettled(
        queries.map(query => fetchSupervisors({groupId, ...query}, SEARCH_QUERY_MAX)),
      )
      if (requestId !== this.searchRequestId) return

      const failures = responses.filter(({status}) => status === 'rejected')
      if (failures.length) {
        Sentry.captureException(new Error('Supervisors search failed'), {
          extra: {groupId, failed: failures.length, total: queries.length, reasons: failures.map(f => f.reason)},
        })
      }

      const results = responses.filter(({status}) => status === 'fulfilled').map(({value}) => value)
      const merged = mergeSupervisors(...results)
      this.searchResults = isSupervisorSearchWidened(term) ? merged.filter(createSupervisorMatcher(term)) : merged
      this.searchResultsTerm = term
      this.searching = false
    },
    selectUser(user) {
      this.$router.push({name: 'User', params: {userId: user.ownerId}})
    },
    getRowClass() {
      return 'click'
    },
  },
}
</script>

<style scoped>
.text-header {
  letter-spacing: 0;
  color: var(--v-gray-lighten5);
  text-align: left;
  font: normal normal 600 16px/20px 'ProximaNovaExCnSBold';
  text-transform: uppercase;
  opacity: 1;
}

.text-item {
  letter-spacing: 0;
  text-align: left;
  font: normal normal normal 16px/26px 'ProximaNovaRegular';
  letter-spacing: 0px;
  color: #000000;
  opacity: 1;
}

.customer-phone {
  font-family: 'ProximaNovaRegular', sans-serif;
  color: var(--v-gray-lighten5);
  font-size: 14px !important;
}

.customer-email {
  font-family: 'ProximaNovaRegular', sans-serif;
  color: var(--v-gray-lighten5);
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
